import {
  DEFAULT_REPAIR_CATEGORIES,
  OTHER_REPAIR_CATEGORY_KEY,
  RepairCategoryDoc,
} from '@otw/models'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import { db } from 'config/firebase'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'

const toCategoryKey = (label: string): string =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

const parseDate = (raw: unknown): Date => {
  if (!raw) return new Date()
  if (typeof raw === 'object' && raw !== null && 'toDate' in raw) {
    const tdate = (raw as { toDate?: () => Date }).toDate
    if (typeof tdate === 'function') return tdate.call(raw)
  }
  return new Date(raw as string)
}

/**
 * Top-level repair categories stored in `repairCategories`. Seeds the collection
 * with `DEFAULT_REPAIR_CATEGORIES` on first read so the picker always has data.
 */
export const useRepairCategories = () => {
  const { userInfo } = useAuth()
  const { showError } = useSnackbar()
  const queryClient = useQueryClient()

  const categoriesCollection = collection(db, 'repairCategories')

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['repairCategories'],
    queryFn: async (): Promise<RepairCategoryDoc[]> => {
      let snapshot = await getDocs(categoriesCollection)

      if (snapshot.empty) {
        await Promise.all(
          DEFAULT_REPAIR_CATEGORIES.map(({ key, label, sortOrder, iconKey }) =>
            setDoc(doc(categoriesCollection, key), {
              key,
              label,
              isBuiltIn: true,
              sortOrder,
              iconKey,
              createdAt: new Date().toISOString(),
              createdBy: 'system',
              updatedAt: null,
              updatedBy: null,
            }),
          ),
        )
        snapshot = await getDocs(categoriesCollection)
      } else {
        const existingKeys = new Set(snapshot.docs.map(docSnap => docSnap.id))
        const missing = DEFAULT_REPAIR_CATEGORIES.filter(({ key }) => !existingKeys.has(key))
        if (missing.length > 0) {
          await Promise.all(
            missing.map(({ key, label, sortOrder, iconKey }) =>
              setDoc(doc(categoriesCollection, key), {
                key,
                label,
                isBuiltIn: true,
                sortOrder,
                iconKey,
                createdAt: new Date().toISOString(),
                createdBy: 'system',
                updatedAt: null,
                updatedBy: null,
              }),
            ),
          )
          snapshot = await getDocs(categoriesCollection)
        }
      }

      return snapshot.docs
        .map(docSnap => {
          const data = docSnap.data() as Partial<RepairCategoryDoc>
          return {
            id: docSnap.id,
            key: data.key ?? docSnap.id,
            label: data.label ?? docSnap.id,
            isBuiltIn: Boolean(data.isBuiltIn),
            sortOrder: data.sortOrder ?? 999,
            iconKey: data.iconKey ?? null,
            createdAt: parseDate(data.createdAt),
            createdBy: data.createdBy ?? '',
            updatedAt: data.updatedAt ? parseDate(data.updatedAt) : null,
            updatedBy: data.updatedBy ?? null,
          } satisfies RepairCategoryDoc
        })
        .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label))
    },
    staleTime: 1000 * 60 * 5,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['repairCategories'] })

  const createCategoryMutation = useMutation({
    mutationFn: async (
      input: string | { label: string; iconKey?: string | null },
    ): Promise<RepairCategoryDoc> => {
      const label = typeof input === 'string' ? input : input.label
      const iconKey = typeof input === 'string' ? null : input.iconKey ?? null
      const trimmed = label.trim()
      if (!trimmed) throw new Error('Category name is required')
      const key = toCategoryKey(trimmed)
      if (!key) throw new Error('Category name must contain at least one alphanumeric character')

      const existing = categories.find(
        c => c.key === key || c.label.toLowerCase() === trimmed.toLowerCase(),
      )
      if (existing) return existing

      const maxSort = categories.reduce((m, c) => Math.max(m, c.sortOrder), 0)
      const sortOrder = maxSort + 10
      const createdBy = userInfo?.id || userInfo?.email || ''
      const createdAt = new Date()

      await setDoc(doc(categoriesCollection, key), {
        key,
        label: trimmed,
        isBuiltIn: false,
        sortOrder,
        iconKey,
        createdAt: createdAt.toISOString(),
        createdBy,
        updatedAt: null,
        updatedBy: null,
      })

      return {
        id: key,
        key,
        label: trimmed,
        isBuiltIn: false,
        sortOrder,
        iconKey,
        createdAt,
        createdBy,
        updatedAt: null,
        updatedBy: null,
      }
    },
    onSuccess: invalidate,
    onError: (error: Error) => showError(`Error creating category: ${error.message}`),
  })

  const updateCategoryMutation = useMutation({
    mutationFn: async ({
      id,
      label,
      iconKey,
    }: {
      id: string
      label?: string
      iconKey?: string | null
    }): Promise<void> => {
      const updatedBy = userInfo?.id || userInfo?.email || ''
      const updates: Record<string, unknown> = {
        updatedAt: new Date().toISOString(),
        updatedBy,
      }
      if (label !== undefined) {
        const trimmed = label.trim()
        if (!trimmed) throw new Error('Category name is required')
        updates.label = trimmed
      }
      if (iconKey !== undefined) updates.iconKey = iconKey
      await updateDoc(doc(categoriesCollection, id), updates as Record<string, never>)
    },
    onSuccess: invalidate,
    onError: (error: Error) => showError(`Error updating category: ${error.message}`),
  })

  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const target = categories.find(c => c.id === id)
      if (!target) throw new Error('Category not found')
      if (target.isBuiltIn) throw new Error('Built-in categories cannot be deleted')
      if (target.key === OTHER_REPAIR_CATEGORY_KEY) throw new Error('The "Other" category cannot be deleted')
      await deleteDoc(doc(categoriesCollection, id))
    },
    onSuccess: invalidate,
    onError: (error: Error) => showError(`Error deleting category: ${error.message}`),
  })

  return {
    categories,
    isLoading,
    createCategory: createCategoryMutation.mutateAsync,
    isCreatingCategory: createCategoryMutation.isPending,
    updateCategory: updateCategoryMutation.mutateAsync,
    isUpdatingCategory: updateCategoryMutation.isPending,
    deleteCategory: deleteCategoryMutation.mutateAsync,
    isDeletingCategory: deleteCategoryMutation.isPending,
  }
}
